// Load embed.js once for the whole app
// metabaseConfig MUST be set on window BEFORE embed.js executes.
import { METABASE_CONFIG } from "../config/metabase.config"

let EMBED_SCRIPT_PROMISE: Promise<void> | null = null

export class MetabaseHelper {
    /**
     * Loads the Metabase embed.js script if it hasn't been loaded already.
     * Returns a promise that resolves when the script is loaded and the custom elements are defined.
     */
    static loadMetabaseEmbedScript(
        instanceUrl = METABASE_CONFIG.instanceUrl,
    ): Promise<void> {
        if (typeof window === "undefined") {
            return Promise.resolve()
        }

        if (EMBED_SCRIPT_PROMISE) {
            return EMBED_SCRIPT_PROMISE
        }

        EMBED_SCRIPT_PROMISE = new Promise((resolve, reject) => {
            // Set config first so embed.js picks it up on load
            window.metabaseConfig = {
                theme: METABASE_CONFIG.theme,
                isGuest: true,
                instanceUrl,
            }

            // If the script tag is already in the DOM (e.g. due to HMR), don't add it twice
            const existing = document.querySelector<HTMLScriptElement>(
                `script[data-metabase-embed="true"]`,
            )

            if (existing) {
                // Resolve when the custom element is registered, regardless of
                // whether the script is mid-load or already finished loading.
                window.customElements
                    .whenDefined("metabase-dashboard")
                    .then(() => {
                        resolve()
                    })

                existing.addEventListener(
                    "error",
                    () => {
                        EMBED_SCRIPT_PROMISE = null // allow retry

                        try {
                            existing.remove()
                        } catch (_e) {}

                        reject(new Error("embed.js failed to load - EXISTING"))
                    },
                    { once: true },
                )

                return
            }

            const script = document.createElement("script")
            script.src = `${instanceUrl}/app/embed.js`
            script.defer = true
            script.dataset.metabaseEmbed = "true"

            script.onload = () => {
                resolve()
            }

            script.onerror = () => {
                EMBED_SCRIPT_PROMISE = null // allow retry

                try {
                    script.remove()
                } catch (_e) {}

                reject(new Error("embed.js failed to load - NEW"))
            }

            document.head.appendChild(script)
        })

        return EMBED_SCRIPT_PROMISE
    }
}
